import React, { useState, useEffect } from 'react';
import { Lock, LogIn, X, Sparkles, Loader, ArrowLeft, SlidersHorizontal } from 'lucide-react';
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { useNavigate } from 'react-router-dom';
import PlaygroundMapComponent from './PlaygroundMapComponent';
import StaticImageSlideshow from './staticimageslideshow';
import ColorSettingsPopup from './ColorSettingsPopup';
import ColorLegend from './ColorLegend';
import PlaygroundImagePreview from './PlaygroundImagePreview';
import { useToast } from './use-toast';
import { FORMATS } from '../config/prices';
import { trackPlaygroundEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Sample playground areas with demo activities
const PLAYGROUND_AREAS = {
  munich: {
    name: 'Munich, Germany',
    coordinates: [48.1351, 11.5820],
    sampleActivities: [
    ]
  },
  innsbruck: {
    name: 'Innsbruck, Austria',
    coordinates: [47.2625, 11.4011],
    sampleActivities: [
    ]
  },
  bregenz: {
    name: 'Bregenz, Austria',
    coordinates: [47.5031, 9.7472],
    sampleActivities: [
    ]
  },
  laspezia: {
    name: 'La Spezia, Italy',
    coordinates: [44.0999, 9.8166],
    sampleActivities: [
    ]
  }
};

const DEFAULT_SETTINGS = {
  km2: 50,
  figure_width: 14,
  figure_height: 10.5,
  placeName: 'Munich, Germany',
  activities: { color: 'sienna', alpha: 0.5, active: true, lineWidth: "normal" },
  buildings: { color: 'darkgrey', alpha: 0.8, active: true },
  forests: { color: 'forestgreen', alpha: 0.1, active: true },
  naturalareas: { color: 'darkgreen', alpha: 0.2, active: true },
  water: { color: 'cornflowerblue', alpha: 0.4, active: true },
  oceans: { color: 'royalblue', alpha: 0.4, active: true },
  streets: { color: 'black', alpha: 0.2, active: true, details: 'roads' },
  border: { color: 'black', alpha: 1, active: false, lineWidth: "normal" },
  land: { color: 'palegoldenrod', alpha: 0.15, active: true },
  topography: true
};

// Default format for playground (4:3 aspect ratio)
const DEFAULT_FORMAT = {
  value: '4:3',
  size: 'XS',
  label: '4:3 XS',
  dimensions: { width: 14, height: 10.5 },
  aspectRatio: 1.333
};

export default function Playground() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedArea, setSelectedArea] = useState('munich');
  const [showOverlay, setShowOverlay] = useState(true);
  const [mapCenter, setMapCenter] = useState(PLAYGROUND_AREAS['munich'].coordinates);
  const [rectangleBounds, setRectangleBounds] = useState(
      [[48.13341414862261, 11.558022777014799], [48.18848604878309, 11.66810484749692]]
  );
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT);
  const [isPortrait, setIsPortrait] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // trackPlaygroundEvents.playgroundEntered();

  const handleAreaChange = (areaKey) => {
    setSelectedArea(areaKey);
    const area = PLAYGROUND_AREAS[areaKey];
    setMapCenter(area.coordinates);

    // Set initial bounds based on area
    let newBounds;
    let placeName;
    if (areaKey === 'innsbruck') {
      newBounds = [[47.21773593542635, 11.299059033639262], [47.31312326457366, 11.486477966360741]];
      setRectangleBounds(newBounds);
      placeName = 'Innsbruck, Austria';
      setSettings(prev => ({
        ...prev,
        km2: 150
      }));
    } else if (areaKey === 'bregenz') {
      newBounds = [[47.47810157913538, 9.591991218601807], [47.57348890828269, 9.780338493312255]];
      setRectangleBounds(newBounds);
      placeName = 'Bregenz, Austria';
      setSettings(prev => ({
        ...prev,
        km2: 150
      }));
    } else if (areaKey === 'laspezia') {
      newBounds = [[44.02038581818282, 9.776188444698455], [44.11577314733013, 9.953196931278113]];
      setRectangleBounds(newBounds);
      placeName = 'La Spezia, Italy';
      setSettings(prev => ({
        ...prev,
        km2: 250
      }));
    } else {
      newBounds = [[48.13341414862261, 11.558022777014799], [48.18848604878309, 11.66810484749692]];
      setRectangleBounds(newBounds);
      setSettings(prev => ({
        ...prev,
        km2: 50
      }));
      placeName = 'Munich, Germany';
    }

    // Update placeName in settings
    setSettings(prev => ({
      ...prev,
      placeName: placeName
    }));

    // Update format based on new bounds
    // updateFormatForArea(newBounds);
  };

  const handleColorSettingChange = (key, subKey, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: {
        ...prevSettings[key],
        [subKey]: value
      }
    }));
  };

  // Initialize format on component mount
  useEffect(() => {
    // Calculate format for initial Innsbruck bounds
    const initialBounds = [[48.01886600568445, 11.434305849183788], [48.11425333483176, 11.62462359417559]];
    // updateFormatForArea(initialBounds);
  }, []);

  // Cleanup image URL on unmount
  useEffect(() => {
    return () => {
      if (generatedImageUrl) {
        URL.revokeObjectURL(generatedImageUrl);
      }
    };
  }, []);

  const handleLoginClick = () => {
    trackPlaygroundEvents.playgroundConvertedToSignup();
    navigate('/login');
  };

  const handleGenerateClick = async () => {
    setIsGenerating(true);
    try {
      const area = PLAYGROUND_AREAS[selectedArea];

      // Get XS dimensions from selectedFormat
      const xsDimensions = selectedFormat.dimensions;
      const finalDimensions = isPortrait
        ? { figure_width: xsDimensions.height, figure_height: xsDimensions.width }
        : { figure_width: xsDimensions.width, figure_height: xsDimensions.height };
      const requestData = {
        settings: {
          ...settings,
          ...finalDimensions
        },
        filteredActivities: area.sampleActivities,
        selectedFormat: selectedFormat,
        rectangleBounds,
        allDates: true,
        dateFrom: null,
        dateTo: null,
        topography: settings.topography || true,
        frame: false,
        frameSettings: {},
        activitySource: 'demo',
        dpi: 250,
        free: true
      };
      const response = await fetch(`${API_URL}/playground/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setGeneratedImageUrl(url);
        trackPlaygroundEvents.playgroundMapGenerated(selectedArea);
        toast({
          title: "Map generated successfully",
          description: "Your demo map has been generated. Click the expand button to view it in full size.",
          className: "bg-black border-white text-white",
          duration: 5000,
        });
      } else {
        trackPlaygroundEvents.playgroundMapError(selectedArea);
        toast({
          title: "Error",
          description: 'Failed to generate your map. Please try again.',
          className: "bg-red-300 text-black",
          variant: "destructive",
        });
      }
    } catch (error) {
      trackPlaygroundEvents.playgroundMapError(selectedArea);
      // console.error('Generation error:', error);
      toast({
        title: "Error",
        description: 'An error occurred while generating your map.',
        className: "bg-red-300 text-black",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Welcome Banner */}
      <div className="bg-white/90 backdrop-blur-sm border-b shadow-sm p-4">
        <div className="container mx-auto max-w-7xl">
          <p className="text-sm text-gray-600 text-center">
            Welcome to the Map Prints Playground! Select a demo area and customize your style below.
          </p>
        </div>
      </div>

      {/* Main Content - Blurred when overlay is shown */}
      <div className={`flex-1 container mx-auto max-w-7xl px-4 py-8 transition-all ${showOverlay ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="flex flex-col gap-8">
          {/* Top Section: Map and Style Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Map Preview */}
            <div className="lg:col-span-1 flex flex-col gap-4">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden h-80 rounded-xl">
                <PlaygroundMapComponent
                  mapCenter={mapCenter}
                  zoom={10}
                  rectangleBounds={rectangleBounds}
                />
              </div>

              {/* Area Selection */}
              <Card className="border-0 rounded-xl shadow-xl">              
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 mb-4">
                <p className="text-xs text-center text-amber-800">
                  Only preselected areas can be used in the playground.
                </p>
              </div>
                <CardContent className="grid grid-cols-2 gap-2">
                  {Object.entries(PLAYGROUND_AREAS).map(([key, area]) => (
                    <button
                      key={key}
                      onClick={() => !showOverlay && handleAreaChange(key)}
                      disabled={showOverlay}
                      className={`p-2 rounded-full border-2 transition-colors font-medium text-xs text-center ${
                        selectedArea === key
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      } ${showOverlay ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {area.name}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right: Style Selection */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              {/* Style Selection */}
              <Card className="flex-1 rounded-xl border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-md text-center">Choose a Style</CardTitle>
                </CardHeader>
                <CardContent>
                  {customizeOpen ? (
                    <div className="flex flex-col">
                      <div className="flex items-center mb-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (expandedCategory) {
                              setExpandedCategory(null);
                            } else {
                              setCustomizeOpen(false);
                            }
                          }}
                          className="flex items-center gap-1 text-gray-700 hover:bg-gray-200 rounded-xl"
                        >
                          <ArrowLeft className="h-4 w-4" />
                          Back
                        </Button>
                      </div>
                      <div className="md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] md:overflow-y-auto pr-1">
                        <ColorSettingsPopup
                          settings={settings}
                          onColorSettingChange={handleColorSettingChange}
                          triggerButton={false}
                          selectedCategory={expandedCategory}
                          onSelectedCategoryChange={setExpandedCategory}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <StaticImageSlideshow
                        onSettingsChange={(newSettings) => {
                          setSettings(prevSettings => ({
                            ...prevSettings,
                            ...newSettings
                          }));
                        }}
                      >
                        <Button
                          size="sm"
                          onClick={() => setCustomizeOpen(true)}
                          className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
                        >
                          <SlidersHorizontal className="h-4 w-4 mr-2" />
                          Customize
                        </Button>
                      </StaticImageSlideshow>

                      <ColorLegend
                        settings={settings}
                        direction="horizontal"
                        onOpenCategory={(category) => {
                          setExpandedCategory(category);
                          setCustomizeOpen(true);
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Bottom Section: Generate Button and Preview */}
          <div className="flex flex-col items-center gap-8">
            {/* Add a seperator */}
            <div className="w-full border-t border-gray-300" />
            <div className="w-full lg:w-[350px]">
              <Button
                onClick={handleGenerateClick}
                size="lg"
                disabled={showOverlay || isGenerating}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader className="mr-2 h-5 w-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate Demo Map
                  </>
                )}
              </Button>
            </div>

            {/* Generated Image Preview */}
            <div className="w-full lg:w-[300px]">
              {generatedImageUrl && (
                <PlaygroundImagePreview
                  imageUrl={generatedImageUrl}
                  onClose={() => setGeneratedImageUrl(null)}
                  isLoading={isGenerating}
                />
              )}
            </div>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-300 w-full rounded-xl lg:max-w-[800px]">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-semibold mb-1">Full Features Locked</p>
                    <p>This is a limited demo. To create your own maps with personal activities, connect Strava/Garmin, and purchase high-quality digital prints, please log in.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Overlay Modal */}
      {showOverlay && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-xl flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowOverlay(false)}
              className="absolute -top-12 right-0 p-2 hover:bg-gray-600 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-6 w-6 text-white hover:text-gray-300" />
            </button>
            <Card className="max-w-md bg-white border-orange-300 rounded-xl">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Lock className="h-12 w-12 text-orange-500" />
                </div>
                <CardTitle className="text-xl text-orange-600">Full Features Locked</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-700 text-center">
                  You're in playground mode! This is a limited demo with sample data. To create and modify your own maps, you need to log in.
                </p>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-1">
                  <p className="text-xs font-semibold text-gray-700">What you can do:</p>
                  <p className="text-xs text-gray-600">✓ View sample maps</p>
                  <p className="text-xs text-gray-600">✓ Test different colors and styles</p>
                  <p className="text-xs font-semibold text-gray-700 mt-2">What requires login:</p>
                  <p className="text-xs text-gray-600">✗ Create your own maps</p>
                  <p className="text-xs text-gray-600">✗ Connect to Strava/Garmin</p>
                  <p className="text-xs text-gray-600">✗ Download and purchase</p>
                </div>
                <Button
                  onClick={() => setShowOverlay(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Explore Playground
                </Button>
                <Button
                  onClick={handleLoginClick}
                  variant="outline"
                  className="w-full"
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Login / Register
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
